import Api from "@/api"
import { useLoading } from "@/composition"
import {
	ExportStageType,
	type Filter,
	type MarriageClaimSort,
	type MarriageSearchOptions,
	type MarriageSearchResults,
	type ObjectSchemaProperty,
	type SendableMarriageExportSearchOptions,
	type SendableMarriageSearchOptions,
} from "@/models"
import { isReactive, reactive, shallowRef, type InjectionKey, type Reactive, type ShallowRef } from "vue"
import { downloadFile, useService } from "vue-utils"
import { convertToSendableColumns, convertToSendableFilters, convertToSendableSort } from "./SearchComponents/Helpers"

export const EditingSearchStateInjectionKey: InjectionKey<EditingSearchState> = Symbol("EditingSearchState")

export function useEditingSearchState() {
	return useService(EditingSearchStateInjectionKey)
}

export class EditingSearchState {
	private runAction: <T>(promise: PromiseLike<T>) => Promise<T>
	private results: Reactive<MarriageSearchResults>
	private columns: ShallowRef<ObjectSchemaProperty[]>
	private searchOptions: Reactive<MarriageSearchOptions>

	constructor(results: MarriageSearchResults, columns: ObjectSchemaProperty[], searchOptions: MarriageSearchOptions) {
		if (!isReactive(results)) {
			throw new Error("Expected reactive search data")
		}
		this.results = results
		this.columns = shallowRef(columns)
		this.searchOptions = reactive(searchOptions)
		const { runAction } = useLoading()
		this.runAction = runAction
	}

	get fullResults() {
		return this.results
	}

	get claims() {
		return this.results.MarriageClaims
	}

	get fullSearchOptions() {
		return this.searchOptions
	}

	get sort() {
		return this.searchOptions.Sort
	}

	get filters() {
		return this.searchOptions.Filters
	}

	get pageSize() {
		return this.searchOptions.PageSize
	}

	get allColumns() {
		return this.columns.value
	}

	set allColumns(newColumns: ObjectSchemaProperty[]) {
		this.columns.value = newColumns
		localStorage.setItem("Columns", JSON.stringify(convertToSendableColumns(this.columns.value)))
	}

	set pageSize(newSize: number) {
		this.searchOptions.PageSize = newSize
		void this.executeSearch()
	}

	async executeSearch() {
		const sendableSearchOptions = reactive<SendableMarriageSearchOptions>({
			PageNumber: this.searchOptions.PageNumber,
			PageSize: this.searchOptions.PageSize,
			Sort: convertToSendableSort(this.searchOptions.Sort),
			Filters: convertToSendableFilters(this.searchOptions.Filters),
		})
		Object.assign(this.results, await this.runAction(Api.marriageClaims.search(sendableSearchOptions)))
	}

	async executeExport(type: ExportStageType) {
		const sendableExportOptions = reactive<SendableMarriageExportSearchOptions>({
			PageNumber: this.searchOptions.PageNumber,
			PageSize: this.searchOptions.PageSize,
			Sort: convertToSendableSort(this.searchOptions.Sort),
			Filters: convertToSendableFilters(this.searchOptions.Filters),
			StageType: type,
		})
		const data = await this.runAction(Api.marriageClaims.exportResults(sendableExportOptions))
		await this.runAction(downloadFile(data, data.headers.get("File-name") ?? "ExportedTaxRebateData.csv"))
	}

	changePage(page: number) {
		this.searchOptions.PageNumber = page
		void this.executeSearch()
	}

	changeSort(newSort: MarriageClaimSort) {
		Object.assign(this.searchOptions.Sort, newSort)
		localStorage.setItem("Sort", JSON.stringify(convertToSendableSort(this.searchOptions.Sort)))
		void this.executeSearch()
	}

	createFilter(createdFilter: Filter) {
		this.searchOptions.Filters.push(createdFilter)
		this.saveFiltersAndSendRequest()
	}

	updateFilter(updatedFilter: Filter, updateIndex: number) {
		this.searchOptions.Filters.splice(updateIndex, 1, updatedFilter)
		this.saveFiltersAndSendRequest()
	}

	removeFilter(removalIndex: number, refreshPage: boolean = true) {
		this.searchOptions.Filters.splice(removalIndex, 1)
		if (refreshPage) {
			this.saveFiltersAndSendRequest()
		}
	}

	clearFilters() {
		this.searchOptions.Filters.splice(0)
		this.saveFiltersAndSendRequest()
	}

	saveFilters() {
		localStorage.setItem("Filters", JSON.stringify(convertToSendableFilters(this.searchOptions.Filters)))
	}

	saveFiltersAndSendRequest() {
		this.saveFilters()
		void this.executeSearch()
	}
}
